<script setup lang="ts">
import { getOrder } from '@/api/order/api';
import { Order } from '@/api/order/types';
import RecapSvg from '@/assets/drawings/recap.svg?component';
import { StateMachineEvent, StateMachineSnapshot } from '@/stateMachine';
import { VueformElement } from '@vueform/vueform';
import { Ref, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

const props = defineProps<{
  snapshot: StateMachineSnapshot;
  send: (event: StateMachineEvent) => void;
}>();

const route = useRoute();

const form$: Ref<VueformElement | null> = ref(null);

const order: Ref<Order | null> = ref(null);
const loading: Ref<boolean> = ref(true);

async function retrieveOrder() {
  const id = route.params.id as string;
  const { data } = await getOrder(parseInt(id));
  order.value = data;
  if (data === null) {
    props.send({
      type: 'error',
      value: 'Order not found',
    });
  } else {
    props.send({
      type: 'order.found',
      value: data,
    });
    props.send({
      type: 'properties.set',
      value: data.procedure,
    });
  }
  loading.value = false;
  if (form$.value) {
    form$.value.update({
      procedure: data.procedure.procedure,
      ...data.procedure.properties,
    });
  }
}

onMounted(() => {
  retrieveOrder();
});
</script>

<template>
  <div class="d-flex h-100">
    <div class="flex-grow-1 text-center d-flex justify-content-center flex-column align-items-center m-3">
      <h2 class="mb-3">Installation</h2>
      <div v-if="loading" class="spinner-border text-secondary" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <div v-else class="w-100">
        <p>#{{ order?.id }} - {{ order?.clientEmail }}</p>
      </div>
      <div class="w-100">
        <Vueform ref="form$" size="md" :display-errors="false" :endpoint="false">
          <SelectElement
            name="procedure"
            :disabled="true"
            :items="[
              {
                value: 'Opticien',
                label: 'Opticien',
              },
              {
                value: 'Ortho',
                label: 'Ortho',
              },
              {
                value: 'Ortho homme',
                label: 'Ortho Home',
              },
              {
                value: 'Oculera',
                label: 'Oculera',
              },
              {
                value: 'Krys',
                label: 'Krys',
              },
              {
                value: 'CDL',
                label: 'CDL',
              },
              {
                value: 'MIA',
                label: 'MIA',
              },
            ]"
            :search="true"
            :native="false"
            label="Procédure"
            input-type="search"
            autocomplete="off"
          />
          <GroupElement name="krys" :conditions="[['procedure', 'in', ['Krys', 'CDL']]]">
            <TextElement name="krys_client_code" label="Code Client" :rules="['regex:/^\\d{6}$/m']" :disabled="true" />
            <TextElement name="krys_wifi_ssid" label="WiFI SSID" :disabled="true" />
            <TextElement name="krys_wifi_password" input-type="password" label="WiFi Password" :disabled="true" />
          </GroupElement>
          <GroupElement name="eyesoft" :conditions="[['procedure', 'in', ['Opticien', 'Ortho']]]">
            <TextElement name="eyesoft_code_client" label="Code Client" :disabled="true" />
          </GroupElement>
          <GroupElement name="oculera" :conditions="[['procedure', 'in', ['Oculera']]]">
            <SelectElement
              name="oculera_version"
              :items="['2.3.0', '2.6.4', 'latest']"
              default="latest"
              :search="true"
              :native="false"
              label="Version"
              input-type="search"
              autocomplete="off"
              :disabled="true"
            />
          </GroupElement>
          <GroupElement name="mia" :conditions="[['procedure', 'in', ['MIA']]]">
            <TextElement name="mia_sender_bundle_id" label="Sender Bundle ID" readonly />
            <TextElement name="mia_product_small_name" label="Product Small Name" readonly />
            <TextElement name="mia_api_uri" label="API URI" readonly />
            <TextElement name="mia_target_pui_version" label="Target PUI Version" readonly />
            <TextElement name="mia_language" label="Language" readonly />
          </GroupElement>
        </Vueform>
      </div>
    </div>
    <div class="d-flex justify-content-center flex-column align-items-center w-50 m-3">
      <RecapSvg />
    </div>
  </div>
</template>

<style scoped></style>
